import type { GenericField, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { Checkbox } from '@neo/primitives/checkbox'
import { Input } from '@neo/primitives/input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { useSheet } from '@shared/hooks/use-sheet'
import { Cog } from 'lucide-react'
import { useEffect, useState } from 'react'
import { websiteScraperSchema } from '../../config'
import type { NewKBFormValues } from '../../types'
import { LinksSkeleton } from './links-skeleton'
import { WebsiteScraperOptions } from './website-scrapper-options'

type KBLink = NewKBFormValues['links'][number]

const OptionsAccordion = ({ form }: { form: GenericFormType<NewKBFormValues> }) => {
  const open = form.useStore((state) => state.values.optionsAccordionOpen)
  const setOpen = (o: string[]) => form.setFieldValue('optionsAccordionOpen', o)
  const urlSelectionType = form.useStore((state) => state.values.url_selection_type)
  const jobId = form.useStore((state) => state.values.jobId)
  const isLoading = form.useStore((state) => state.values.isLoading)
  const isCrawled = !!jobId

  if (isLoading) return null
  // 'all' ("sync entire source") still needs the Limit / Max depth controls so the user can tune
  // (or expand) the crawl instead of being silently capped — show them inline, same as the
  // pre-crawl 'selected' state.
  if (urlSelectionType === 'all') return <WebsiteScraperOptions form={form} />
  if (urlSelectionType === 'selected' && !isCrawled) return <WebsiteScraperOptions form={form} />
  return (
    <Accordion type="multiple" value={open} onValueChange={setOpen}>
      <AccordionItem value="options" className="border-none">
        <AccordionTrigger className="hover:no-underline justify-start gap-1 text-p-600">
          <div className="flex items-center gap-1 ">
            <Cog className="w-4 h-4" />
            <span className="text-p-600">View applied filters</span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <WebsiteScraperOptions form={form} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

const SelectAllCheckbox = ({ form }: { form: GenericFormType<NewKBFormValues> }) => {
  const links = form.useStore((state) => state.values.links)
  const [selectAll, setSelectAll] = useState(false)
  useEffect(() => {
    setSelectAll(links.every((link) => link.selected))
  }, [links])
  return (
    <Checkbox
      label="Select all"
      name="select-all"
      id="select-all"
      onChange={(checked) => {
        form.setFieldValue(
          'links',
          links.map((link) => ({ ...link, selected: checked })),
        )
      }}
      value={selectAll}
      className="text-p-500"
      labelClassName="text-p-500 text-primary"
    />
  )
}
const LinksComponent = ({ form }: { form: GenericFormType<NewKBFormValues> }) => {
  const isLoading = form.useStore((state) => state.values.isLoading)
  const links = form.useStore((state) => state.values.links)
  const { openSheet } = useSheet()
  if (isLoading) return <LinksSkeleton form={form} />
  if (form.state.values.url_selection_type === 'all' || links.length === 0) return null
  return (
    <div className="bg-bg3 rounded-md p-2 min-h-0 flex flex-col">
      <div className="flex gap-2 justify-between items-center border-b st-2 pb-2 px-4 mb-2">
        <SelectAllCheckbox form={form} />
        <span className="text-xs-400 text-secondary">
          Scraped {form.state.values.links.length} pages in total
        </span>
      </div>
      {/* Cap the Radix viewport (h-full) directly — see app-document-tree: max-h on the Root alone
          doesn't bound an h-full viewport, so a large crawl's page list would overrun the layout. */}
      <ScrollArea className="pr-2 -mr-2" viewportClassName="max-h-[500px]">
        {form.state.values.links.map((link: KBLink, index: number) => (
          <div
            key={link.url}
            className="px-4 hover:bg-hv2 rounded-md py-1 cursor-pointer group relative"
          >
            <div className="flex items-center gap-2">
              <Checkbox
                name={`links.${index}.selected`}
                id={`links.${index}.selected`}
                onChange={(checked) => {
                  form.setFieldValue(`links[${index}].selected`, checked)
                  form.validateField('links', 'change')
                }}
                value={link.selected}
                className="flex-shrink-0"
                data-testid="checkbox-link-select"
                label=""
              />
              <div className="flex-1 min-w-0 pr-[80px]">
                <p className="text-s-500 truncate" title={link.title}>
                  {link.title}
                </p>
                <p
                  className={`font-jetbrains ${link.title ? 'text-xs-400 text-secondary' : 'text-xs-500'} truncate`}
                >
                  {link.url}
                </p>
              </div>
            </div>
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <Button
                size="xs"
                variant="ghost"
                type="button"
                onClick={() => openSheet(link)}
                className="hidden group-hover:block"
                data-testid="button-preview"
              >
                Preview
              </Button>
            </div>
          </div>
        ))}
      </ScrollArea>
    </div>
  )
}
// Field Configuration
export const websiteScraperFields: GenericField<NewKBFormValues>[] = [
  {
    name: 'url',
    type: 'custom',
    component: ({ onChange, value, form }) => (
      <div className="flex gap-2">
        <form.Subscribe
          selector={(state) => [
            state.values.isLoading,
            state.values.url,
            state.values.max_pages,
            state.values.max_depth,
            state.values.crawl_subpages,
          ]}
          children={([isLoading]) => (
            <Input
              label="Source URL"
              disabled={isLoading as boolean}
              inputMode="url"
              placeholder="Enter website URL to scrape"
              onChange={onChange}
              value={value as string}
              className="flex-1"
            />
          )}
        />
      </div>
    ),
    eventDataExtractor: (event) => event.target.value,
    zodValidation: websiteScraperSchema.shape.url,
  },
  {
    name: 'options',
    type: 'basic',
    component: OptionsAccordion,
  },
  {
    name: 'link-renderer',
    type: 'basic',
    component: LinksComponent,
  },
]
